import React, { useEffect, useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import { TailSpin } from "react-loader-spinner";

import type { InviteCodeApi } from "../../lib/api/inviteCodes";
import { inviteStatusBadgeClass } from "./inviteCodesTable";
import type { InviteCodeUiStatus } from "./inviteCodesTypes";

type Props = {
  open: boolean;
  code: InviteCodeApi | null;
  onClose: () => void;
  deactivating?: boolean;
  onDeactivate: (id: string, reason: string) => void;
};

function eligibilityLabel(v: string): string {
  const u = v.toUpperCase();
  if (u === "NEW_USERS") return "New Users";
  if (u === "EXISTING_USERS") return "Existing Users";
  if (u === "ALL") return "All Users";
  return v;
}

function normalizeStatus(raw: string): InviteCodeUiStatus {
  const u = raw.toUpperCase();
  if (u === "ACTIVE") return "active";
  if (u === "USED") return "used";
  if (u === "EXPIRED") return "expired";
  if (u === "DEACTIVATED") return "deactivated";
  return "active";
}

function deactivationReason(code: InviteCodeApi): string | null {
  const v = code.deactivationReason;
  return v && v.trim() ? v : null;
}

function ownerLabel(code: InviteCodeApi): string {
  const p = code.ownerUserId?.profile;
  if (p?.firstName || p?.lastName) {
    return [p.firstName, p.lastName].filter(Boolean).join(" ");
  }
  return code.ownerUserId?.email?.address || "—";
}

const InviteCodeDetailModal: React.FC<Props> = ({
  open,
  code,
  onClose,
  deactivating,
  onDeactivate,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open, code?._id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deactivating) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, deactivating]);

  if (!open || !code) return null;

  const status = normalizeStatus(String(code.status));
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  const totalPts = (code.depositReward || 0) + (code.tradeReward || 0);
  const members = code.stats?.members ?? code.memberCount ?? 0;
  const deactReason =
    status === "deactivated" ? deactivationReason(code) : null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-black/75 p-4 font-manrope backdrop-blur-[3px]"
      role="presentation"
      onClick={() => {
        if (!deactivating) onClose();
      }}
    >
      <div
        className="relative my-auto w-full max-w-[460px] overflow-hidden rounded-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-[#2C363E] px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#617889]">
              Invite code
            </p>
            <h2
              id="invite-detail-title"
              className="mt-1 truncate font-sora text-lg font-bold text-white"
            >
              {code.code}
            </h2>
            <span
              className={`mt-2 inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${inviteStatusBadgeClass(
                status
              )}`}
            >
              {statusLabel}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={deactivating}
            className="rounded-xl p-2 text-[#869AA9] transition hover:bg-[#273037] hover:text-white disabled:opacity-50"
            aria-label="Close"
          >
            <FaTimesCircle className="text-2xl" />
          </button>
        </header>

        <div className="space-y-3 px-5 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Total reward
              </dt>
              <dd className="mt-1 text-[15px] font-semibold text-white">
                {totalPts} pts
              </dd>
            </div>
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Split
              </dt>
              <dd className="mt-1 text-[13px] text-[#AEBCC6]">
                Deposit {code.depositReward} / Trade {code.tradeReward}
              </dd>
            </div>
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Min deposit
              </dt>
              <dd className="mt-1 text-[15px] font-semibold text-white">
                ${code.minDepositUSD}
              </dd>
            </div>
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Min trade
              </dt>
              <dd className="mt-1 text-[15px] font-semibold text-white">
                ${code.minTradeUSD}
              </dd>
            </div>
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3 sm:col-span-2">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Eligible
              </dt>
              <dd className="mt-1 text-[14px] font-medium text-secondary_button">
                {eligibilityLabel(String(code.eligibility || "ALL"))}
              </dd>
            </div>
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Inviter / owner
              </dt>
              <dd className="mt-1 text-[14px] text-[#E7EBEE]">{ownerLabel(code)}</dd>
            </div>
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Members
              </dt>
              <dd className="mt-1 text-[14px] text-[#E7EBEE]">
                {members} / {code.maxMembers || "—"}
              </dd>
            </div>
            <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3 sm:col-span-2">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Expiry
              </dt>
              <dd className="mt-1 text-[14px] text-[#E7EBEE]">
                {code.expiresAt
                  ? new Date(code.expiresAt).toLocaleString()
                  : "Never"}
              </dd>
            </div>
            {Array.isArray(code.requiredPairs) &&
              code.requiredPairs.length > 0 && (
                <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3 sm:col-span-2">
                  <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                    Required pairs
                  </dt>
                  <dd className="mt-1 text-[14px] text-[#E7EBEE]">
                    {code.requiredPairs.join(", ")}
                  </dd>
                </div>
              )}
            {deactReason && (
              <div className="rounded-lg border border-red-500/25 bg-red-500/5 px-3 py-3 sm:col-span-2">
                <dt className="text-[10px] font-semibold uppercase tracking-wide text-red-300/70">
                  Deactivation reason
                </dt>
                <dd className="mt-1 text-[14px] text-[#E7EBEE]">
                  {deactReason}
                </dd>
              </div>
            )}
            {code.stats && (
              <div className="rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 py-3 sm:col-span-2">
                <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                  Stats
                </dt>
                <dd className="mt-1 text-[13px] text-[#AEBCC6]">
                  Completed {code.stats.completed ?? 0} · Points awarded{" "}
                  {code.stats.totalPointsAwarded ?? 0} · Claim rate{" "}
                  {code.stats.claimRate ?? 0}%
                </dd>
              </div>
            )}
          </dl>

          {status === "active" && (
            <div className="mt-2 space-y-2">
              <label
                htmlFor="invite-deactivate-reason"
                className="block text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]"
              >
                Reason for deactivation
              </label>
              <textarea
                id="invite-deactivate-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={deactivating}
                rows={2}
                placeholder="e.g. Code abused, campaign ended…"
                className="w-full resize-none rounded-xl border border-[#2C363E] bg-[#161E25]/80 px-3 py-2.5 text-[13px] text-[#E7EBEE] placeholder:text-[#5C6B76] focus:border-red-500/40 focus:outline-none disabled:opacity-60"
              />
              <button
                type="button"
                disabled={deactivating || !reason.trim()}
                onClick={() => onDeactivate(code._id, reason.trim())}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 text-[13px] font-semibold text-red-200 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deactivating ? (
                  <TailSpin color="#fecaca" height={20} width={20} />
                ) : null}
                Deactivate code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteCodeDetailModal;
